/**
 * Member profile fields screen (DNKADM-0058) — the `v2_member_profile_field` registry
 * list + create/edit/deactivate/reactivate (handoff §5.13). `RequireAdmin`-gated route +
 * a Configuration nav item ("Member profile fields").
 *
 * The registry is the catalog the member profile editor (0059) reads to decide, per
 * field, pass-through vs read-only. Loads `listMemberProfileFields({ status })` through
 * `useResource`, keyed on the Active/Inactive/All filter (`useActiveFilter` +
 * `<ActiveFilter>`, §4.3). A table of Label → Type → Ownership → Member-writable →
 * contact-system key → status with Edit + Deactivate/Reactivate. "Add field" and Edit
 * open the {@link MemberProfileFieldDialog}. Every write refetches + toasts. A first-run
 * empty tenant (§8 — the table may legitimately start empty) shows a strong empty state
 * with a create CTA.
 *
 * White-label (§4.0): the **Ownership** column renders via `fieldOwnershipLabel`
 * (`GHL` → "External / contact system", `DINK` → "Managed here") and the **contact-system
 * key** column is headed via `CONTACT_SYSTEM_FIELD_KEY_LABEL` — DATA, never a rendered
 * brand. This screen SUPERSEDED the v1 custom-fields/classifications model
 * (`src/features/custom-fields/*`), now retired (DNKADM-0074).
 *
 * **Outcome framing (DNKADM-0270).** This tab is how an operator chooses what
 * members can edit on their own profile: flipping a field to "Members can edit"
 * is the ONLY thing that gives members a Preferences card (dink-journix
 * DNKJRX-0220 hides the card entirely when no field is member-editable). The
 * intro/empty-state copy states that outcome, the column term matches the
 * dialog's toggle ("Members can edit"), and the empty state's primary CTA opens
 * the dialog with the toggle pre-set ON (the guided path for the GO-LIVE WS1
 * fields — preferred resource, handedness, …).
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * label/key search and the Active/Inactive/All filter; every data column has a shared
 * {@link SortableHeader} (default Label ascending). Search, filter and sort live in the
 * URL (`memberProfileFieldsListView.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import { fieldOwnershipLabel, CONTACT_SYSTEM_FIELD_KEY_LABEL } from '@/utils/whitelabel';
import {
  deactivateMemberProfileField,
  fieldTypeLabel,
  listMemberProfileFields,
  reactivateMemberProfileField,
  type MemberProfileField,
} from '@/api/memberProfileFields';
import { MemberProfileFieldDialog } from './MemberProfileFieldDialog';
import {
  MEMBER_PROFILE_FIELD_DEFAULT_SORT,
  MEMBER_PROFILE_FIELD_SORT_KEYS,
  MEMBER_PROFILE_FIELDS_URL_DEFAULTS,
  fieldLabelOf,
  memberProfileFieldMatches,
  memberProfileFieldSortValue,
  type MemberProfileFieldSortKey,
} from './memberProfileFieldsListView';
import { ScreenError } from '@/components/ScreenError';

export function MemberProfileFieldsScreen() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  // DNKADM-0430: search, status filter and sort come from the URL
  // (`memberProfileFieldsListView.ts`).
  const url = useListUrlState(MEMBER_PROFILE_FIELDS_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(
    url.values.sort,
    url.values.dir,
    MEMBER_PROFILE_FIELD_SORT_KEYS,
    MEMBER_PROFILE_FIELD_DEFAULT_SORT,
  );

  const fields = useResource(
    useCallback(() => listMemberProfileFields({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Dialog state: `null` = closed; `{ field: null }` = add; `{ field }` = edit.
  // `memberEditable` seeds the guided empty-state path (toggle pre-set ON).
  const [dialog, setDialog] = useState<{
    field: MemberProfileField | null;
    memberEditable?: boolean;
  } | null>(null);

  const refetch = fields.refetch;
  const existingKeys = (fields.data ?? []).map((f) => f.fieldKey).filter((k) => k !== '');

  const visibleRows = useMemo(
    () =>
      sortRows(
        (fields.data ?? []).filter((field) => memberProfileFieldMatches(field, search)),
        (field) => memberProfileFieldSortValue(field, sort.key),
        sort.dir,
        (field) => field.id,
      ),
    [fields.data, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: MemberProfileFieldSortKey) =>
      setUrl(toSortParams(toggleSort(sort, key), MEMBER_PROFILE_FIELD_DEFAULT_SORT)),
    [setUrl, sort],
  );

  const handleDeactivate = useCallback(
    async (field: MemberProfileField) => {
      try {
        await deactivateMemberProfileField(field.id);
        refetch();
        showToast('Field deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the field.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (field: MemberProfileField) => {
      try {
        await reactivateMemberProfileField(field.id);
        refetch();
        showToast('Field reactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the field.', 'error');
      }
    },
    [refetch, showToast],
  );

  const labelOf = fieldLabelOf;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          {/* DNKADM-0499: this screen is the CRM page's third tab, under the page's h1. */}
          <Typography variant="h6" component="h2">
            Fields members can edit
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: 640 }}>
            Choose which profile details members can edit on their own profile. Fields
            marked “Members can edit” appear in the member’s Preferences card — until at
            least one is, members see no Preferences card at all.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setDialog({ field: null })}
          sx={{ flexShrink: 0 }}
        >
          Add field
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search fields',
          disabled: fields.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter fields by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {fields.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading member profile fields"
          sx={{ borderRadius: 3 }}
        />
      )}

      {fields.status === 'error' && (
        <ScreenError
          error={fields.error}
          what="the member profile fields"
          onRetry={fields.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {fields.status === 'ready' && fields.data && fields.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA. The registry may
        // legitimately start empty (no per-tenant fields registered yet).
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="badge" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {filter.value === 'active'
              ? 'Members can’t edit anything yet'
              : 'No fields match this filter'}
          </Typography>
          {filter.value === 'active' && (
            <>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary', maxWidth: 460, mx: 'auto' }}
              >
                Add a field members can edit — a preferred resource, handedness, anything
                they should manage themselves — and it appears in their Preferences card.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="add" />}
                onClick={() => setDialog({ field: null, memberEditable: true })}
              >
                Add a member-editable field
              </Button>
            </>
          )}
        </Paper>
      )}

      {fields.status === 'ready' && fields.data && fields.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Member profile fields" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Label" col="label" sort={sort} onSort={onSort} />
                <SortableHeader label="Type" col="type" sort={sort} onSort={onSort} />
                <SortableHeader label="Ownership" col="ownership" sort={sort} onSort={onSort} />
                <SortableHeader
                  label="Members can edit"
                  col="editable"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  label={CONTACT_SYSTEM_FIELD_KEY_LABEL}
                  col="fieldKey"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((field) => (
                <TableRow key={field.id}>
                  <TableCell>{labelOf(field)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {fieldTypeLabel(field.fieldType)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {fieldOwnershipLabel(field.fieldOwnership)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={field.isMemberWritable ? 'Yes' : 'No'}
                      color={field.isMemberWritable ? 'success' : 'default'}
                      variant={field.isMemberWritable ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {field.ghlCustomFieldKey ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={field.isActive ? 'Active' : 'Inactive'}
                      color={field.isActive ? 'success' : 'default'}
                      variant={field.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconAction
                      size="small"
                      label={`Edit field ${labelOf(field)}`}
                      onClick={() => setDialog({ field })}
                    >
                      <Icon name="edit" />
                    </IconAction>
                    {field.isActive ? (
                      <IconAction
                        size="small"
                        label={`Deactivate field ${labelOf(field)}`}
                        onClick={() => void handleDeactivate(field)}
                      >
                        <Icon name="toggle_off" />
                      </IconAction>
                    ) : (
                      <IconAction
                        size="small"
                        label={`Reactivate field ${labelOf(field)}`}
                        onClick={() => void handleReactivate(field)}
                      >
                        <Icon name="toggle_on" />
                      </IconAction>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {fields.status === 'ready' &&
        fields.data &&
        fields.data.length > 0 &&
        visibleRows.length === 0 && <ListNoMatch noun="fields" query={search} />}

      <MemberProfileFieldDialog
        open={dialog !== null}
        field={dialog?.field ?? null}
        existingKeys={existingKeys}
        initialMemberEditable={dialog?.memberEditable ?? false}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
          showToast('Field saved', 'success');
        }}
        showToast={showToast}
      />
    </Box>
  );
}
